import React from 'react'
import Head from 'components/Head'
import SiteLayout from 'components/SiteLayout'
import { usePageView } from 'hooks'
import { getStaticWrapper } from 'utils'

const PrivacyPolicy = () => {
  usePageView()
  const updatedAt = 'June 23, 2022'
  return (
    <SiteLayout hideBanner>
      <Head title='Privacy Policy | Barstool Sports' />
      <section className='container'>
        <article className='staticPage'>
          <header className='staticPage__header'>
            <h1>Privacy Policy</h1>
          </header>
          <section className='staticPage__content'>
            <p>
              <em>Last updated: {updatedAt}</em>
            </p>
            <p>
              At Barstool, the privacy and security of your information is a top priority. We want you to understand how
              we handle your data. We also want you to know your rights and choices.
            </p>
            <p>
              This Privacy Policy (“Policy”) describes how we handle your data when you use the services listed here:
            </p>
            <ul className='list--bulleted'>
              <li>Barstool Sports website and the Barstool Store</li>
              <li>our affiliated websites, such as Balls Beachwear</li>
              <li>our newsletters</li>
              <li>our mobile apps, such as One Bite</li>
              <li>our OTT apps such as Barstool Bets and Old Row</li>
              <li>all related social media pages</li>
              <li>anywhere else we gather information about you and refer to this Policy</li>
            </ul>
            <p>For simplicity, we refer to all these services as the “Platform” in this Policy.</p>
            <p>
              How we handle your information depends on which Platform services you use, and how you use them. This
              Policy explains the types of information we collect when you use the Platform, how we use information, how
              information is shared with other parties, and your rights and controls over information about you. We
              encourage you to read this Policy carefully.
            </p>
            <p>
              The Platform operates from the United States, but can be accessed worldwide. Parts of the Policy apply
              only to residents of specific areas. California residents should consult the{' '}
              <a href='#california'>Rights Of California Users</a> section. Residents of the European Economic Area
              (“EEA”), the United Kingdom or Switzerland should consult the sections of this policy relating to the{' '}
              <a href='#eea'>Rights of EEA Residents</a> and <a href='#international'>International Data Transfers</a>{' '}
              sections.
            </p>
            <p>
              Barstool Sports, Inc. operates the Platform and is referred to in this Policy as “Barstool”, “we”, “our”
              or “us”. By using or accessing the Platform, you signify that you have read, understand and agree to be
              bound by this Policy. This Policy is effective as of July 31, 2022 and was last modified prior to this
              version on December 31, 2019. For previous versions of this Policy, please{' '}
              <a href='https://chumley.barstoolsports.com/barstool-sports/Privacy%20Policy%20Dec.%202019.pdf'>
                click here
              </a>
              .
            </p>
            <p>
              Advertising targeted to you is described under{' '}
              <a href='#how-we-share-information'>How We Share Information</a> and in our{' '}
              <a href='/cookie-policy'>Cookie Policy</a>. Because the Platform is always changing, this Policy may
              change over time. We will notify you before any material changes take effect so you have time to review
              them.
            </p>

            <h2>Information We Collect</h2>

            <h3>Information you give us</h3>

            <p>
              You may use many of our websites and apps and interact with their content without providing any
              information about yourself. However, to use some aspects of the Platform, we will need information about
              you, such as if you:
            </p>
            <ul className='list--bulleted'>
              <li>Establish an account</li>
              <li>Purchase products</li>
              <li>Order food through one of our apps, such as One Bite</li>
              <li>Contact us or otherwise communicate with us in any way</li>
              <li>Subscribe or opt-in to our newsletters, alerts, or other communications</li>
              <li>Submit content</li>
              <li>Participate in a contest or promotion and/or redeem any prize you may win</li>
            </ul>
            <p>
              When you submit information through the Platform, whether provided to enable a service or disclosed
              through comments, you are consenting to its collection, use and disclosure in accordance with this Policy.
            </p>
            <p>
              Please note that if you make a purchase through our Platform, your payment information, like your credit
              card details, is only collected and processed our payment processing partner(s).
            </p>

            <h3>Information collected when you use the Platform</h3>
            <p>
              As you use the Platform, we generate technical data about which features you’ve used, how you’ve used them
              and the devices you use to access our services, including:
            </p>
            <p>
              (1) “Device Information” relating to the computer or device you use to interact with our Platform, such as
              your IP address, your advertising IDs (which are randomly generated numbers that you can reset by going
              into your device’s settings, including Apple IDFA or an Android Advertising ID), your browser and
              operating system, your internet service provider, and your device and browser settings.
            </p>
            <p>
              (2) “Usage Data” related to your use of the Platform, such as the pages you visit, the sites you use
              before or after visiting ours, your actions within the Platform, the content or advertisements you
              interact with, general location information, time stamps and performance logs and reports.
            </p>

            <h2>Information We Receive From Others</h2>

            <h3 id='from-our-affiliates'>From our affiliates</h3>
            <p>
              We also help operate certain third-party domains in cooperation with affiliates, such as Balls Beachwear
              (such businesses, together with Penn National Gaming, Inc., our “Affiliates”). We may use anonymous,
              internal identifiers to unify information about your interactions with the Platform and Affiliates’
              services. We may also receive information about you from our partners where Barstool ads are published on
              a partner’s service (in which case they may pass along details on a campaign’s performance).
            </p>

            <h3>From social networks</h3>
            <p>
              If you choose to connect to our Platform through a third-party social network such as Facebook, Twitter,
              Instagram, or Tik Tok (each, a “Social Network”), we may collect information from your profile on such
              Social Network, such as your name, username, and e-mail address, and we will use that information for the
              purposes set forth herein. In addition, our Platform may offer social sharing features which will allow
              you to “Share” “Like” or otherwise interact or engage on a Social Network. If you decide to use such
              features, it may allow the sharing and collection of Information both to and from such Social Network so
              you should check the privacy policy of each Social Network before using such features.
            </p>

            <h3>From Cookies And Similar Data Technology</h3>
            <p>
              Please see our <a href='/cookie-policy'>Cookie Policy</a> for a detailed explanation of how we use cookies
              and how you can exercise control over them. Still, applicable law requires us to mention a few specifics
              in this Policy as well:
            </p>
            <p>
              (1) If you sign up for our text messaging program(s), cookies may be used to personalize your experience
              (e.g., send you personalized text messages such as shopping cart reminders).
            </p>
            <p>
              (2) Finally, although some browsers offer a “do not track” (“DNT”) option, no common industry standard for
              DNT exists. Therefore, we have not configured the Platform to respond to browsers’ DNT signals.
            </p>
            <p>
              By using the Platform, you understand and agree that the Platform will track your activities and use of
              the Platform as described in the <a href='/cookie-policy'>Cookie Policy</a>, consistent with and subject
              to the cookie settings you apply through the Platform and your device.
            </p>

            <h2 id='how-we-use-information'>How We Use Information</h2>

            <p>
              The main reason we use your information is to deliver and improve our content and services. Additionally,
              we use your info to communicate with you, and to provide you with advertising that may be of interest to
              you. Here is detailed explanation of the various reasons for which we use your information, together with
              practical examples:
            </p>
            <p>(1) To provide our content and products to you, including via your account</p>
            <ul className='list--bulleted'>
              <li>Create and manage your account</li>
              <li>Provide you with customer support and respond to your requests</li>
              <li>Complete your orders (such as your Barstool Store orders)</li>
              <li>Communicate with you about our services</li>
            </ul>

            <p>(2) To manage your account or fulfill product orders</p>
            <ul className='list--bulleted'>
              <li>Register you on the services to allow you to purchase goods or services</li>
              <li>Administer your account on new features and apps</li>
            </ul>
            <p>(3) To improve our services and develop new ones</p>
            <ul className='list--bulleted'>
              <li>Administer focus groups, market studies and surveys</li>
              <li>Review interactions with customer teams to improve our quality of service</li>
              <li>Develop new content and services</li>
            </ul>
            <p>(4) To operate advertising and marketing campaigns</p>
            <ul className='list--bulleted'>
              <li>Administer sweepstakes, contests, discounts or other offers</li>
              <li>
                Text you, after you have given your consent and only until you withdraw it, about offers, including on a
                personalized basis.
              </li>
              <li>
                Customize advertising to you based on technical data and information about you in deidentified or
                hashed, non-human readable form
              </li>
              <li>
                Perform and measure the effectiveness of advertising campaigns on our services and marketing campaigns
                off of the Platform
              </li>
              <li>Communicate with you about products or services that we believe may interest you</li>
            </ul>
            <p>(5) To prevent, detect and fight fraud and other illegal or unauthorized activities</p>
            <ul className='list--bulleted'>
              <li>
                Find and address ongoing, suspected or alleged violations of our 
                <a href='/terms-of-use'>Terms of Use</a>
              </li>
              <li>
                Better understand and design countermeasures against violations of our 
                <a href='/terms-of-use'>Terms of Use</a>
              </li>
              <li>
                Retain data related to violations of our <a href='/terms-of-use'>Terms of Use</a> to prevent against
                recurrences
              </li>
              <li>
                Enforce or exercise our rights; for example, those our <a href='/terms-of-use'>Terms of Use</a>
              </li>
            </ul>
            <p>(6) To ensure legal compliance</p>
            <ul className='list--bulleted'>
              <li>Comply with legal requirements</li>
              <li>Assist law enforcement</li>
            </ul>

            <h3 id='legal-bases'>Legal Bases</h3>
            <p>To process your information as described in this Policy, we rely on the following legal bases:</p>
            <p>
              (1) Provide our services to you: The reason we process your information for purposes 1, 2 and 3 above is
              to perform the contract that you have with us. For instance, if you order products from us, we use your
              information to complete the purchase and ship your good. Further, as you discover content on the Platform,
              we may tailor the content to your interests.
            </p>
            <p>
              (2) Legitimate interests: We process your information for purposes 3, 4 and 5 above, based on our
              legitimate interest. For instance, we analyze users’ behavior on our services to continuously improve our
              offerings, we suggest offers we think might interest you and promote our own services, we process
              information to help keep our members safe and we process data where necessary to enforce our rights,
              assist law enforcement and enable us to defend ourselves in the event of a legal action.
            </p>
            <p>
              (3) Comply with applicable laws and regulations: We process your information for purpose 6 above where it
              is necessary for us to comply with applicable laws and regulations and evidence our compliance with
              applicable laws and regulations. For example, we retain traffic data and data about transactions in line
              with our accounting, tax and other statutory data retention obligations and to be able to respond to valid
              access requests from law enforcement.{' '}
            </p>
            <p>
              (4) Consent: If you choose to provide us with other personal information, including any that may be
              considered “special” or “sensitive” under applicable law, you’re consenting to our processing of that
              information in accordance with this Policy. From time to time, we may ask for your consent to collect
              specific information such as your precise geolocation or use your information for certain specific
              reasons. In some cases, you may withdraw your consent by adapting your settings (for instance, cookie
              settings). In any case, you may withdraw your consent at any time by contacting us at the address provided
              at the end of this Policy.
            </p>

            <h2 id='how-we-share-information'>How We Share Information</h2>
            <p>
              Since our goal is to help you discover great content and services, the principal sharing of your
              information is to enhance your experience of the Platform. This section describes how and why we share
              information with Affiliates, service providers and partners and for certain purposes, like consensual
              direct marketing and for legal reasons.
            </p>
            <p>(1) With our Affiliates</p>
            <p>
              As noted above under <a href='#from-our-affiliates'>From Our Affiliates</a>, we may share, link or pool
              user information among Affiliates, but always in accordance with applicable law, applicable agreements and
              this Policy.
            </p>
            <p>(2) Direct marketing</p>
            <p>
              We only disclose, rent, sell or share any information about you to third parties for direct marketing
              purposes unless you opt in, and will only do so until you opt out.
            </p>
            <p>
              We may share information about you with third party sponsors or partners who will use it for marketing
              purposes but only if you opt in to such sharing or do not opt out when prompted. We will never share
              information in this manner without giving you one of these two options.
            </p>
            <p>(3) Functional sharing</p>
            <p>
              In addition to the use of trackers described in our <a href='/cookie-policy'>Cookie Policy</a>, we
              contract with companies or individuals to provide certain services related to the functionality and
              features of the Platform, including payment processing, email and hosting services, software development,
              food ordering/delivery, shipping and fulfillment, data management, surveys and marketing, and
              administration of contests and other promotions. We call them our “Service Providers.” We may share
              information about you, as well as Usage Data and Device Information, with Service Providers as appropriate
              for them to perform their services for us and our Service Providers are permitted to use your information
              only for such purposes.
            </p>
            <p>(4) For legal reasons</p>
            <p>
              Finally, we may share your Information: (i) In response to subpoenas, court orders, or other legal
              process; to establish or exercise our legal rights; to defend against legal claims; or as otherwise
              required by law. In such cases we reserve the right to raise or waive any legal objection or right
              available to us; (ii) When we believe it is appropriate to investigate, prevent, or take action regarding
              illegal or suspected illegal activities; to protect and defend the rights, property, or safety of our
              company, our users, or others; and in connection with the enforcement of our{' '}
              <a href='/terms-of-use'>Terms of Use</a> and other agreements; or (iii) In connection with a corporate
              transaction, such as a divestiture, merger, consolidation, or asset sale, or in the unlikely event of
              bankruptcy.{' '}
            </p>
            <p>(5) With your consent or at your request</p>
            <p>
              We may ask for your consent to share your information with third parties. In any such case, we will make
              it clear why we want to share the information.
            </p>
            <p>(6) Technical</p>
            <p>
              Finally, we may use and share non-personal information (meaning information that, by itself, does not
              identify who you are, such as device information, general demographics, general behavioral data,
              geolocation in de-identified form), as well as personal information in hashed, non-human readable form,
              under any of the above circumstances. We may also share this information with other Affiliates and third
              parties (notably advertisers) to develop and deliver targeted advertising on our services and on websites
              or applications of third parties, and to analyze and report on advertising you see. We may combine this
              information with additional non-personal information or personal information in hashed, non-human readable
              form collected from other sources. More information on our use of cookies and similar technologies can be
              found in our Cookie Policy.
            </p>

            <h2>How Long We Retain Your Information</h2>
            <p>
              We retain your information only as long as we need it for our legitimate business purposes (as described
              under <a href='#how-we-use-information'>How We Use Information</a>) and as permitted by applicable law.
            </p>

            <h2>Your Rights</h2>
            <h3>In general</h3>
            <p>
              We want you to be in control of your information, so we want to remind you of the following options and
              tools available to you:
            </p>
            <p>(1) Opting Out of Communications</p>
            <p>
              As described above, we may use the information we collect from you to send you newsletters, push
              notifications, general communications and promotional e-mails, including promotional communications about
              our Platform and our or our partners’ products or services. If you do not want to receive such
              communications, you can opt out (as applicable) by using the unsubscribe link at the bottom of our
              communications, unchecking the applicable box to opt out when prompted, or by simply not opting in when
              prompted. You may also at any time opt out of receiving communications from us by sending an e-mail to
              privacy@barstoolsports.com with the subject line “Opt Out”.
            </p>
            <p>
              Please note that even if you unsubscribe from our communications, we may still need to contact you with
              important information related to your account and your purchases. For example, even if you have
              unsubscribed from our promotional emails, we will still send you confirmation of a purchase you have made
              on our Platform or to alert you to changes to our <a href='/terms-of-use'>Terms of Use</a> or Privacy
              Policy.
            </p>
            <p>(2) Disallowing Cookies and Location Data Collection</p>
            <p>
              Our <a href='/cookie-policy'>Cookie Policy</a> explains how you can control your cookie settings. You can
              exercise that control on our desktop and/or mobile website by clicking here or where indicated in the
              Cookie Policy. Some of our apps allow granular cookie control as well, but device-level settings about
              cookie use within our apps may afford broader or simpler control.
            </p>
            <p>
              When you access certain services, like some of our apps, through a mobile device, you may be asked to
              share your precise (GPS level) geo-location information with us so we can customize your experience and
              the content you receive. You may change location sharing settings for each app at the device level at any
              time.
            </p>
            <p>(3) Opting Out of Targeted Advertising</p>
            <p>
              You have a choice about participating in interest-based advertising. If you wish to opt out, you have a
              few options:
            </p>

            <ul className='list--bulleted'>
              <li>
                On your mobile device, you can visit https://youradchoices.com/appchoices to learn about and download
                the Digital Advertising Alliance’s opt-out app, which allows you to opt your mobile device out of
                interest-based advertising from participating companies.
              </li>
              <li>
                Your mobile device settings may allow you to limit the use of information from your device in
                interest-based advertising through a “Limit Ad Tracking” setting (in an iOS device) or an “Opt Out of
                Interest-Based Ads” setting (on an Android device).
              </li>
              <li>
                You can learn more about advertising networks and interest-based advertising, and your ability to opt
                out, by visiting the Digital Advertising Alliance at www.aboutads.info/choices or the Network
                Advertising Initiative at www.networkadvertising.org/choices.
              </li>
            </ul>
            <p>
              In providing you with transparency and access to choice regarding interest-based advertising, we are
              acting in accordance with our commitment to the Digital Advertising Alliance’s Self-Regulatory Principles.
              You can learn more about these Principles <a href='www.aboutads.info/consumers'>here</a>.
            </p>

            <h3 id='eea'>Rights of EEA Residents</h3>
            <p>
              This section of the Privacy Policy is applicable to residents of the EEA, the United Kingdom, Switzerland
              (referred to here as “EEA Residents”).
            </p>
            <p>
              We process the “personal information” (as defined by GDPR) of EEA Residents in accordance with the General
              Data Protection Regulation (2016/679) of the European Parliament and of the Council of 27 April 2016
              (“GDPR”). For the purposes of GDPR in the EEA, the United Kingdom and Switzerland, the data controller is
              Barstool Sports, Inc., 333 7th Avenue, Floors 2-3, New York, NY 10001.
            </p>
            <p>
              The purpose and the legal bases for collecting and processing personal information of EEA Residents is
              described under <a href='#how-we-use-information'>How We Use Information</a> and{' '}
              <a href='#legal-bases'>Legal Bases</a>. EEA Residents may exercise rights under GDPR as described under
              Your Right to Access, Review, and Delete Information about You. If you do not provide personal information
              to us or withdraw consent for processing personal information, we may not be able to provide you with
              certain aspects of the Platform.
            </p>

            <h3 id='international'>International data transfers</h3>
            <p>
              If you are resident outside the United States, including in the EEA, we transfer information about you for
              processing in the United States. Under the GDPR framework, we are considered a “controller” and a
              “co-processor” of personal data of EEA Residents. By providing information about you to enable Platform
              services, you consent to the processing of such data in the United States. The transfer of this
              information to the United States is necessary for the performance of a contract between you and us for
              your use of the Platform.
            </p>
            <p>
              When we transfer EEA resident information outside of Barstool, we use standard contract clauses (standard
              contractual clauses are commitments between companies transferring personal data, binding them to protect
              the privacy and security of your data) or another appropriate transfer mechanism. We are currently in the
              process of reviewing transfers to our vendors and the associated legal bases in response to the recent
              Court of Justice for the European Union’s ruling on transfers of personal data to the{' '}
              <a href='https://curia.europa.eu/juris/document/document.jsf;jsessionid=D9783B09CDC25934085215EB6CAC9FDB?text=&docid=228677&pageIndex=0&doclang=EN&mode=req&dir=&occ=first&part=1&cid=14024666'>
                United States
              </a>
              , as well as the forthcoming{' '}
              <a href='https://www.whitehouse.gov/briefing-room/statements-releases/2022/03/25/fact-sheet-united-states-and-european-commission-announce-trans-atlantic-data-privacy-framework/'>
                Trans-Atlantic Data Privacy Framework
              </a>
              .
            </p>
            <p>
              Please note that the United States does not have data protection laws equivalent to those in the EEA and
              other jurisdictions.
            </p>

            <h3 id='california'>Rights of California Consumers</h3>
            <p>
              This section describes your rights as an individual or household in California (“California consumers”).
            </p>
            <ul className='list--bulleted'>
              <li>Rights under the California Consumer Privacy Act (“CCPA”)</li>
            </ul>
            <p>
              CCPA provides California consumers with additional rights regarding their “personal information” (as
              defined in CCPA). The categories of information we collect are generally described above, and depend on
              how you use the Platform.
            </p>
            <p>Under CCPA, California consumers have the following rights:</p>
            <p>(1) Right to Know and Right to Delete.</p>
            <p>
              A California consumer has the right to request that we disclose what personal information we collect, use,
              disclose and sell. A California consumer also has the right to submit requests to delete personal
              information.
            </p>
            <p>
              When we receive a valid request to know or delete from a California consumer, we will confirm receipt of
              the request within 10 days and provide information about how we will process the request, including our
              verification process. We will respond to such requests within 45 days.
            </p>
            <p>(2) Right for Disclosure of Information.</p>
            <p>
              A California consumer may also submit requests that we disclose specific types or categories of “personal
              information” that we collect.
            </p>
            <p>
              Under certain circumstances, we will not provide such “personal information,” including where the
              disclosure creates a substantial, articulable and unreasonable risk to the security of that information ,
              customers’ account with us, or the security of our systems or networks. We do not disclose California
              consumers’ account passwords and security questions and answers.
            </p>
            <p>(3) Submitting Requests</p>
            <p>
              If you are a California consumer and would like to make any requests under the CCPA, please contact us as
              described under{' '}
              <a href='#your-right-to-access'>Your Right to Access, Review, and Delete Information About You</a>. Your
              request must provide sufficient information to identify yourself as a California consumer, such as name,
              e-mail address, home or work address, or other information that is on record with us. Do not provide
              social security numbers, driver’s license numbers, account numbers, credit or debit card numbers, medical
              information or health information with requests.
            </p>
            <p>(4) Verifying Requests</p>
            <p>
              We verify requests by first confirming the source of the request and then by matching the information
              submitted to the information we maintain. If requests are unclear or submitted through means other than
              outline above, we will provide the requestor with direction on how to remedy the deficiencies. We will use
              other appropriate measures to verify requests received by mail or telephone.
            </p>
            <p>If we cannot verify the identity of the requestor, we may deny the request.</p>
            <ul className='list--bulleted'>
              <li>Do Not Sell My Info.</li>
            </ul>

            <p>
              If you are a California consumer and would like to opt out of the sale of your Personal Information (as
              defined under CCPA), please go to this link: Do Not Sell My Info or emailing privacy@barstoolsports.com
              with the subject line “CCPA - Do Not Sell My Info”.
            </p>
            <ul className='list--bulleted'>
              <li>Civil Code Section 1798.83</li>
            </ul>

            <p>
              Because we do only give your information to third parties for direct marketing with your consent, and
              always give you the right to opt out after opting in, we believe we are not currently required to comply
              with California Civil Code Section 1798.83.{' '}
            </p>
            <ul className='list--bulleted'>
              <li>California Do Not Track Disclosures</li>
            </ul>

            <p>
              Although some browsers offer a “do not track” (“DNT”) option, no common industry standard for DNT exists.
              Therefore, we have not configured the Platform to respond to browsers’ DNT signals.
            </p>
            <h3 id='your-right-to-access'>Your Right to Access, Review, and Delete Information about You</h3>
            <p>
              Under certain laws, including as described above with respect to the GDPR and CCPA, you may have the right
              to: obtain confirmation that we hold information about you, request access to and receive that
              information, update and correct inaccuracies in that information, object to the continued processing of
              information about you, and have the information blocked, anonymized or deleted, as appropriate. The right
              to access may be limited in some circumstances by local law, including as described above under California
              law. If you qualify, in order to exercise these rights, please contact us as described under How to{' '}
              <a href='#contact-us'>Contact Us</a>.
            </p>
            <p>
              We may ask you to provide additional information for identity verification purposes, or to verify that you
              are in possession of an applicable email account.
            </p>
            <p>
              Please understand, however, that we reserve the right to retain an archive of any information about you
              for a commercially reasonable time to ensure that its deletion does not affect the integrity of our data;
              and we further reserve the right to retain any deidentified data derived from such information.
            </p>
            <h2>Changes To Privacy Policy</h2>
            <p>
              We may need to change this Policy at any time. The current version of the Policy will remain posted on
              this area of our Platform with the date such modifications were made. We will inform you of any material
              changes to this Policy, but please review this Policy from time to time, in case you miss our
              notification, and so that you are aware when non-material changes are made. If you have any questions
              about changes to this Policy, please contact us at privacy@barstoolsports.com and include “Information
              Regarding Updated Policy” in the subject line.{' '}
            </p>
            <p>
              Your continued use of the Platform after such change constitutes your acceptance of the then-current
              Policy, and if you do not accept it, you may choose not to use the Platform.
            </p>
            <h2>Children Under 13</h2>
            <p>
              The Platform is intended for and targeted to adults. We do not knowingly collect or solicit information
              directly from anyone under the age of 13. If you are under 13, please do not send any information about
              yourself to us, including your name, address, telephone number, or email address. In the event that we
              learn that we have collected information from a child under age 13, we will delete that information as
              quickly as possible. If you are a parent or guardian of a child under 13 years of age and you believe your
              child has provided us with information, please contact us at privacy@barstoolsports.com.
            </p>
            <h2 id='contact-us'>How to Contact Us</h2>
            <p>
              If you have questions or comments about this Policy, please contact us at privacy@barstoolsports.com with
              “Privacy” in the subject line, or write to us at:
            </p>
            <p>
              Barstool Sports, Inc.
              <br />
              333 7th Avenue, Floors 2-3
              <br />
              New York, NY 10001
              <br />
              Attn: Legal Department
            </p>
          </section>
        </article>
      </section>
    </SiteLayout>
  )
}

export const getStaticProps = getStaticWrapper()

export default PrivacyPolicy
